import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Stack,
  Text,
  Group,
  Badge,
  Card,
  Button,
  ThemeIcon,
  Box,
  Progress,
} from "@mantine/core";
import {
  IconCheck,
  IconVideo,
  IconUpload,
  IconShieldCheck,
  IconUserCheck,
  IconClipboardList,
  IconMessage,
  IconArrowLeft,
  IconClock,
  IconUser,
} from "@tabler/icons-react";
import PageContent from "../shared/PageContent/PageContent";

function RepProgramProgressPage() {
  const { programId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Get data from navigation state
  const { programName, user, userProgress } = location.state || {};

  const getTaskTypeIcon = taskType => {
    switch (taskType) {
      case "VIDEO":
        return <IconVideo size={20} />;
      case "UPLOAD":
        return <IconUpload size={20} />;
      case "USER_APPROVE":
        return <IconUserCheck size={20} />;
      case "ADMIN_APPROVE":
        return <IconShieldCheck size={20} />;
      case "TEXT_RESPONSE":
        return <IconMessage size={20} />;
      default:
        return <IconClipboardList size={20} />;
    }
  };

  const formatDateTime = dateString => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  const completedCount = userProgress?.completed_tasks_count || 0;
  const totalCount = userProgress?.total_tasks_count || 0;
  const completionPct =
    totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const userName = user
    ? `${user.first_name} ${user.last_name}`
    : "Unknown User";

  return (
    <PageContent
      title={
        <Box>
          <Group gap="xs" mb="xs">
            <ThemeIcon size="md" radius="xl" variant="light" color="blue">
              <IconUser size={16} />
            </ThemeIcon>
            <Text fw={600} size="lg">
              {userName}
            </Text>
          </Group>
          <Text size="sm" c="dimmed">
            {user?.email}
          </Text>
        </Box>
      }
    >
      <Stack gap="lg">
        {/* Back Button */}
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(`/downline/programs/${programId}`)}
          style={{ alignSelf: "flex-start" }}
        >
          Back to {programName || "Program"}
        </Button>

        {/* Progress Overview Card */}
        <Card padding="xl" radius="md" withBorder shadow="sm">
          <Stack gap="md">
            <Text size="lg" fw={600}>
              Progress Overview
            </Text>

            <Group justify="space-around" grow>
              <Box>
                <Group gap="xs" mb="xs">
                  <ThemeIcon
                    size="sm"
                    radius="xl"
                    variant="light"
                    color="green"
                  >
                    <IconCheck size={14} />
                  </ThemeIcon>
                  <Text size="sm" c="dimmed" fw={500}>
                    Completed
                  </Text>
                </Group>
                <Text size="2xl" fw={700}>
                  {completedCount}
                </Text>
              </Box>

              <Box>
                <Group gap="xs" mb="xs">
                  <ThemeIcon size="sm" radius="xl" variant="light" color="blue">
                    <IconClipboardList size={14} />
                  </ThemeIcon>
                  <Text size="sm" c="dimmed" fw={500}>
                    Total Tasks
                  </Text>
                </Group>
                <Text size="2xl" fw={700}>
                  {totalCount}
                </Text>
              </Box>

              <Box>
                <Group gap="xs" mb="xs">
                  <ThemeIcon
                    size="sm"
                    radius="xl"
                    variant="light"
                    color="violet"
                  >
                    <IconClock size={14} />
                  </ThemeIcon>
                  <Text size="sm" c="dimmed" fw={500}>
                    Last Activity
                  </Text>
                </Group>
                <Text size="lg" fw={700}>
                  {userProgress?.tasks?.find(t => t.is_completed)
                    ? formatDateTime(
                        userProgress.tasks
                          .filter(t => t.is_completed && t.completed_at)
                          .sort(
                            (a, b) =>
                              new Date(b.completed_at) -
                              new Date(a.completed_at)
                          )[0]?.completed_at
                      )
                    : "—"}
                </Text>
              </Box>
            </Group>

            <Box mt="md">
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>
                  Overall Progress
                </Text>
                <Text size="lg" fw={700} c="violet">
                  {completionPct}%
                </Text>
              </Group>
              <Progress
                value={completionPct}
                color={completionPct === 100 ? "green" : "orange"}
                size="xl"
                radius="xl"
              />
            </Box>
          </Stack>
        </Card>

        {/* Task Details Section */}
        <Box>
          <Text size="lg" fw={600} mb="md">
            Task Details
          </Text>

          {userProgress?.tasks && userProgress.tasks.length > 0 ? (
            <Stack gap="sm">
              {userProgress.tasks.map((task, index) => (
                <Card
                  key={task.id || index}
                  shadow="xs"
                  padding="md"
                  radius="md"
                  withBorder
                  style={{
                    backgroundColor: task.is_completed ? "#f8f9fa" : "white",
                  }}
                >
                  <Group align="flex-start" gap="md" wrap="nowrap">
                    {/* Task Icon */}
                    <Box c={task.is_completed ? "green" : "gray"} mt={2}>
                      {task.is_completed ? (
                        <ThemeIcon size="lg" radius="xl" color="green">
                          <IconCheck size={20} />
                        </ThemeIcon>
                      ) : (
                        getTaskTypeIcon(task.task_type)
                      )}
                    </Box>

                    {/* Task Details */}
                    <Box style={{ flex: 1 }}>
                      <Group justify="space-between" align="flex-start" mb="xs">
                        <Box style={{ flex: 1 }}>
                          <Group gap="xs" mb="xs">
                            <Text fw={600} size="md">
                              {task.task_name || `Task ${index + 1}`}
                            </Text>
                            {task.is_completed && (
                              <Badge color="green" size="sm" variant="light">
                                <Group gap={4}>
                                  <IconCheck size={12} />
                                  <span>Completed</span>
                                </Group>
                              </Badge>
                            )}
                            {task.requires_admin_approval && (
                              <Badge color="orange" size="sm" variant="light">
                                Approval Required
                              </Badge>
                            )}
                            {task.approved_by && (
                              <Badge color="green" size="sm" variant="light">
                                Approved
                              </Badge>
                            )}
                          </Group>

                          {task.task_desc && (
                            <Text size="sm" c="dimmed" mb="xs">
                              {task.task_desc}
                            </Text>
                          )}

                          {task.is_completed && task.completed_at && (
                            <Group gap="xs" mt="xs">
                              <IconClock size={14} color="gray" />
                              <Text size="xs" c="dimmed">
                                Completed {formatDateTime(task.completed_at)}
                              </Text>
                              {task.completed_by && (
                                <>
                                  <Text size="xs" c="dimmed">
                                    •
                                  </Text>
                                  <Group gap={4}>
                                    <IconUser size={14} />
                                    <Text size="xs" c="dimmed">
                                      {task.completed_by}
                                    </Text>
                                  </Group>
                                </>
                              )}
                            </Group>
                          )}

                          {/* Show user response for TEXT_RESPONSE tasks */}
                          {task.task_type === "TEXT_RESPONSE" &&
                            task.is_completed &&
                            task.user_text_response && (
                              <Box
                                mt="md"
                                p="md"
                                style={{
                                  backgroundColor: "white",
                                  borderRadius: "8px",
                                  border: "1px solid #dee2e6",
                                }}
                              >
                                <Text size="xs" fw={600} c="dimmed" mb="xs">
                                  User Response:
                                </Text>
                                <Text
                                  size="sm"
                                  style={{ whiteSpace: "pre-wrap" }}
                                >
                                  {task.user_text_response}
                                </Text>
                              </Box>
                            )}
                        </Box>
                      </Group>
                    </Box>
                  </Group>
                </Card>
              ))}
            </Stack>
          ) : (
            <Card padding="xl" withBorder>
              <Text size="md" c="dimmed" ta="center">
                No tasks available for this program
              </Text>
            </Card>
          )}
        </Box>
      </Stack>
    </PageContent>
  );
}

export default RepProgramProgressPage;
